import React, { useState, useEffect } from 'react';
import { 
  getOrderDocuments, 
  addOrderDocument, 
  updateOrderDocument, 
  deleteOrderDocument,
  uploadFile 
} from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  Plus, 
  FileCheck, 
  FileX, 
  Upload, 
  MoreHorizontal, 
  Edit, 
  Trash2,
  Download,
  CheckCircle,
  Clock,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '@/lib/utils';

export default function OrderDocumentChecklist({ orderId, readOnly = false }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [formData, setFormData] = useState({
    document_name: '',
    is_required: true,
    is_received: false,
    notes: ''
  });

  useEffect(() => {
    if (orderId) loadDocuments();
  }, [orderId]);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const res = await getOrderDocuments(orderId);
      setDocuments(res.data || []);
    } catch (error) {
      console.error('Failed to load documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (doc = null) => {
    if (doc) {
      setEditingDoc(doc);
      setFormData({
        document_name: doc.document_name || '',
        is_required: doc.is_required !== false,
        is_received: doc.is_received || false,
        notes: doc.notes || ''
      });
    } else {
      setEditingDoc(null);
      setFormData({
        document_name: '',
        is_required: true,
        is_received: false,
        notes: ''
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.document_name.trim()) {
      toast.error('Document name is required');
      return;
    }

    try {
      if (editingDoc) {
        await updateOrderDocument(orderId, editingDoc.id, formData);
        toast.success('Document updated');
      } else {
        await addOrderDocument(orderId, formData);
        toast.success('Document added');
      }
      setDialogOpen(false);
      loadDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save document');
    }
  };

  const handleToggleReceived = async (doc) => {
    try {
      const newReceived = !doc.is_received;
      await updateOrderDocument(orderId, doc.id, {
        is_received: newReceived,
        received_date: newReceived ? new Date().toISOString().split('T')[0] : null
      });
      toast.success(newReceived ? 'Marked as received' : 'Marked as not received');
      loadDocuments();
    } catch (error) {
      toast.error('Failed to update document');
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm('Delete this document from the checklist?')) return;
    try {
      await deleteOrderDocument(orderId, doc.id);
      toast.success('Document removed');
      loadDocuments();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const handleFileUpload = async (doc, file) => {
    try {
      const res = await uploadFile(file, 'order_document', doc.id);
      await updateOrderDocument(orderId, doc.id, {
        file_id: res.data.file_id,
        file_name: file.name
      });
      toast.success('File uploaded');
      loadDocuments();
    } catch (error) {
      toast.error('Failed to upload file');
    }
  };

  const receivedCount = documents.filter(d => d.is_received).length;
  const requiredCount = documents.filter(d => d.is_required).length;
  const requiredReceived = documents.filter(d => d.is_required && d.is_received).length;

  const getStatusInfo = () => {
    if (requiredCount === 0) return { color: 'bg-slate-100 text-slate-600', text: 'No requirements' };
    if (requiredReceived === requiredCount) return { color: 'bg-emerald-100 text-emerald-700', text: 'All received' };
    if (requiredReceived > 0) return { color: 'bg-amber-100 text-amber-700', text: `${requiredReceived}/${requiredCount} received` };
    return { color: 'bg-red-100 text-red-700', text: 'Pending documents' };
  };

  const statusInfo = getStatusInfo();

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <div className="animate-spin w-6 h-6 border-2 border-teal-500 border-t-transparent rounded-full mx-auto" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="order-document-checklist">
      <CardHeader className="flex flex-row items-center justify-between pb-4">
        <div className="flex items-center gap-3">
          <CardTitle className="text-lg">Document Checklist</CardTitle>
          <Badge className={statusInfo.color}>{statusInfo.text}</Badge>
        </div>
        {!readOnly && (
          <Button size="sm" onClick={() => openDialog()} data-testid="add-checklist-doc">
            <Plus className="w-4 h-4 mr-1" /> Add Document
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {documents.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <FileCheck className="w-10 h-10 mx-auto mb-2 text-slate-300" />
            <p className="text-sm">No documents in checklist</p>
            {!readOnly && (
              <Button variant="link" onClick={() => openDialog()} className="mt-2">
                Add first document
              </Button>
            )}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"></TableHead>
                <TableHead>Document</TableHead>
                <TableHead className="text-center">Required</TableHead>
                <TableHead className="text-center">Received</TableHead>
                <TableHead>File</TableHead>
                {!readOnly && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {documents.map((doc) => (
                <TableRow key={doc.id} data-testid="checklist-doc-row">
                  <TableCell>
                    {doc.is_received ? (
                      <CheckCircle className="w-5 h-5 text-emerald-500" />
                    ) : doc.is_required ? (
                      <AlertCircle className="w-5 h-5 text-red-500" />
                    ) : (
                      <Clock className="w-5 h-5 text-slate-400" />
                    )}
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium">{doc.document_name}</p>
                      {doc.notes && <p className="text-xs text-slate-500">{doc.notes}</p>}
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant={doc.is_required ? 'default' : 'secondary'}>
                      {doc.is_required ? 'Yes' : 'Optional'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    {!readOnly ? (
                      <Checkbox
                        checked={doc.is_received}
                        onCheckedChange={() => handleToggleReceived(doc)}
                        data-testid="doc-received-checkbox"
                      />
                    ) : (
                      <Badge variant={doc.is_received ? 'default' : 'secondary'}>
                        {doc.is_received ? 'Yes' : 'No'}
                      </Badge>
                    )}
                    {doc.received_date && (
                      <p className="text-xs text-slate-500 mt-1">{formatDate(doc.received_date)}</p>
                    )}
                  </TableCell>
                  <TableCell>
                    {doc.file_name ? (
                      <div className="flex items-center gap-2">
                        <span className="text-sm truncate max-w-[150px]">{doc.file_name}</span>
                        <Button variant="ghost" size="sm">
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : !readOnly ? (
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          className="hidden"
                          onChange={(e) => {
                            if (e.target.files?.[0]) {
                              handleFileUpload(doc, e.target.files[0]);
                            }
                          }}
                        />
                        <Button variant="ghost" size="sm" asChild>
                          <span><Upload className="w-4 h-4 mr-1" /> Upload</span>
                        </Button>
                      </label>
                    ) : (
                      <span className="text-slate-400 text-sm">No file</span>
                    )}
                  </TableCell>
                  {!readOnly && (
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openDialog(doc)}>
                            <Edit className="w-4 h-4 mr-2" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDelete(doc)} className="text-red-600">
                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingDoc ? 'Edit Document' : 'Add Document to Checklist'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label>Document Name *</Label>
              <Input
                value={formData.document_name}
                onChange={(e) => setFormData(p => ({ ...p, document_name: e.target.value }))}
                placeholder="e.g., Passport Copy"
                data-testid="doc-name-input"
              />
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="is_required"
                  checked={formData.is_required}
                  onCheckedChange={(v) => setFormData(p => ({ ...p, is_required: v }))}
                />
                <Label htmlFor="is_required">Required Document</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="is_received"
                  checked={formData.is_received}
                  onCheckedChange={(v) => setFormData(p => ({ ...p, is_received: v }))}
                />
                <Label htmlFor="is_received">Already Received</Label>
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData(p => ({ ...p, notes: e.target.value }))}
                placeholder="Optional notes"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} data-testid="save-checklist-doc">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
